import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Clock, Minus } from "lucide-react";
import Datatable from "./tables/Datatable";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  redirectUser,
  updateUserStatus,
  verifyUsers, // ✅ Import verifyUsers
} from "../redux/slices/userslice";
import Loader from "./Loader";
import Swal from "sweetalert2";
import ToggleSwitch from "./Buttons/Togglebtn";
import icon1 from "../assets/imgs/svgs/icon.svg";
import icon2 from "../assets/imgs/svgs/icon2.svg";
import { Link, ExternalLink } from "lucide-react";
import Switch from "../assets/imgs/svgs/Switch.svg";
import toast from "react-hot-toast";

const Table = () => {
  const dispatch = useDispatch();
  const {
    data,
    status,
    totalPages: serverTotalPages,
  } = useSelector((state) => state.userlist);

  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageLoading, setPageLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const isTableLoading = pageLoading;
  const [localStatus, setLocalStatus] = useState({});
  const [localVerifyStatus, setLocalVerifyStatus] = useState({}); // ✅ New state for verify status

  const token = useSelector((state, loading) => state.auth.token);

  // Fetch users whenever page or entriesPerPage changes
  useEffect(() => {
    if (!token) return;

    dispatch(
      fetchUsers({ page, perPage: entriesPerPage, search: searchQuery }),
    ).finally(() => {
      setInitialLoad(false);
    });
  }, [dispatch, page, entriesPerPage, searchQuery, token]);

  const onHandleRedirect = (encryptedId) => {
    const payload = {
      user_id: encryptedId,
    };

    dispatch(redirectUser(payload))
      .unwrap()
      .then((res) => {
        if (res?.status && res?.data?.userAccessToken) {
          const token = res.data.userAccessToken;
          const adminId = res.data.admin_id;
          const userId = res.data.user_id;

          const tokenKey = `user_token_${userId}`;
          const redirectUrl = `https://partner.tiffintap.com/login?token=${token}&token_key=${tokenKey}&admin_id=${adminId}`;
          window.open(redirectUrl, "_blank");
        } else {
          toast.error(res?.message || "Token nahi mila");
        }
      })
      .catch((err) => {
        console.log("API ERROR:", err);
        toast.error("Something went wrong");
      });
  };

  // Merge local status, verify status and add serial numbers
  const mergedData = useMemo(() => {
    if (!data) return [];
    return data.map((item, index) => ({
      ...item,
      srNo: (page - 1) * entriesPerPage + index + 1,
      status: localStatus[item.id] ?? item.status,
      is_verify: localVerifyStatus[item.id] ?? item.is_verify, // ✅ Merge verify status
    }));
  }, [data, localStatus, localVerifyStatus, page, entriesPerPage]);

  const handleToggle = async (user_id, currentStatus) => {
    const newStatus = !currentStatus;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${newStatus ? "activate" : "deactivate"} this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    });

    if (!result.isConfirmed) return;

    setLocalStatus((prev) => {
      const updated = { ...prev, [user_id]: newStatus };
      return updated;
    });

    try {
      const response = await dispatch(
        updateUserStatus({
          user_id,
          is_confirmed: 1,
        }),
      ).unwrap();

      if (response.success) {
        toast.success(
          newStatus
            ? "User Activated Successfully!"
            : "User Deactivated Successfully!",
        );
      } else {
        setLocalStatus((prev) => ({ ...prev, [user_id]: currentStatus }));
        toast.error("Failed to update user status");
      }
    } catch (err) {
      setLocalStatus((prev) => ({ ...prev, [user_id]: currentStatus }));
      Swal.fire("Error!", err || "Failed to update user status.", "error");
    }
  };

  // ✅ New handler for verify toggle
  const handleVerifyToggle = async (user_id, currentVerifyStatus) => {
    const newVerifyStatus = !currentVerifyStatus;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${newVerifyStatus ? "verify" : "unverify"} this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    });

    if (!result.isConfirmed) return;

    // Optimistic UI update
    setLocalVerifyStatus((prev) => ({
      ...prev,
      [user_id]: newVerifyStatus,
    }));

    try {
      const response = await dispatch(
        verifyUsers({
          user_id,
          is_confirmed: newVerifyStatus ? 1 : 0, // ✅ Send 1 for verify, 0 for unverify
        }),
      ).unwrap();

      if (response.success) {
        toast.success(
          newVerifyStatus
            ? "User Verified Successfully!"
            : "User Unverified Successfully!",
        );
      } else {
        // Revert on failure
        setLocalVerifyStatus((prev) => ({
          ...prev,
          [user_id]: currentVerifyStatus,
        }));
        toast.error("Failed to update verify status");
      }
    } catch (err) {
      // Revert on error
      setLocalVerifyStatus((prev) => ({
        ...prev,
        [user_id]: currentVerifyStatus,
      }));
      Swal.fire("Error!", err || "Failed to update verify status.", "error");
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= serverTotalPages) {
      setPageLoading(true);
      setPage(newPage);
    }
  };

  useEffect(() => {
    if (status === "succeeded" || status === "failed") {
      setPageLoading(false);
    }
  }, [status]);

  // Handle entries per page change
  const handleEntriesChange = (newLimit) => {
    setPageLoading(true);
    setEntriesPerPage(newLimit);
    setPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(1);
    setPageLoading(true);
  };

  const columns = [
    {
      header: "Sr. No.",
      accessor: "srNo",
      render: (value) => <span>{value}</span>,
    },
    {
      header: "Action",
      accessor: "status",
      render: (status, row) => (
        <div className="flex justify-center">
          <ToggleSwitch
            checked={row.status}
            onChange={() => {
              handleToggle(row.id, row.status);
            }}
            onColor="#99e68f"
            offColor="#ff7f7f"
            handleColor="#fff"
            width={40}
            height={20}
            handleDiameter={15}
            showStatus={false}
          />
        </div>
      ),
    },
    {
      header: "Detail",
      accessor: "detail",
      render: (detail) => (
        <div className="flex gap-2">
          <div className="flex justify-center items-center">
            <span className="bg-[#fff0ec] text-[#ff7448] font-bold text-sm px-3 py-2 rounded-md">
              {(() => {
                if (!detail?.name) return "";

                const words = detail.name.trim().split(/\s+/);

                if (words.length === 1) {
                  return words[0].slice(0, 2).toUpperCase();
                }

                return (words[0][0] + words[1][0]).toUpperCase();
              })()}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-xs"> {detail.name}</span>
            <span className="font-bold text-xs">{detail.email}</span>
            <span className="font-bold text-xs"> {detail.mobile}</span>
          </div>
        </div>
      ),
    },
    {
      header: "No of Orders",
      accessor: "total_orders",
      render: (val) => <span>{val ?? 0}</span>,
    },
    {
      header: "No of Meals",
      accessor: "total_meals",
      render: (val) => (
        <span className="text-sm bg-gray-100 rounded-md p-2 justify-center flex gap-2">
          <img src={icon1} alt="icon1" />
          {val ?? 0}
        </span>
      ),
    },
    {
      header: "No of Customers",
      accessor: "total_customers",
      render: (val) => (
        <span className="text-sm bg-gray-100 rounded-md p-2 justify-center flex gap-2">
          <img src={icon2} alt="icon1" />
          {val ?? 0}
        </span>
      ),
    },
    {
      header: "Switch to Account",
      accessor: "encrypted_id",
      render: (_, row) => (
        <>
          <div className="text-sm bg-gray-100 rounded-md p-2 flex justify-center gap-4 cursor-pointer">
            <span
              onClick={() => onHandleRedirect(row.encrypted_id)}
              className="hover:bg-orange-200 rounded-xl p-2 transition-all duration-300"
            >
              <img src={Switch} alt="switch" className="h-3 w-3" />
            </span>
          </div>
        </>
      ),
    },
    {
      header: "Providers Url",
      accessor: "providers_link",
      render: (_, row) => (
        <div className="text-sm bg-gray-100 rounded-md p-2 flex justify-center gap-4">
          {!row.providers_link ? (
            <span className="text-gray-400 text-xs">
              <Minus size={15} className="text-orange-500" />
            </span>
          ) : (
            <span
              onClick={() => window.open(row.providers_link, "_blank")}
              className="hover:bg-orange-200 rounded-xl p-2 transition-all duration-300 cursor-pointer"
            >
              <ExternalLink size={15} className="text-orange-500" />
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Last Login",
      accessor: "last_app_logged_in",
      render: (_, row) => (
        <div className="flex flex-col">
          <span className="flex items-center text-xs text-black">
            {row.last_app_logged_in?.date || "-"}
          </span>
          <span className="flex items-center text-xs text-black">
            {row.last_app_logged_in?.time || "-"}
          </span>
        </div>
      ),
    },
    // ✅ New Column: Verify User (Added after Last Login)
    {
      header: "Verify User",
      accessor: "is_verify",
      render: (is_verify, row) => (
        <div className="flex justify-center">
          <ToggleSwitch
            checked={row.is_verify === 1 || row.is_verify === true} // ✅ Handle both 1/0 and true/false
            onChange={() => {
              handleVerifyToggle(row.id, row.is_verify);
            }}
            onColor="#4CAF50"
            offColor="#ff7f7f"
            handleColor="#fff"
            width={40}
            height={20}
            handleDiameter={15}
            showStatus={false}
          />
        </div>
      ),
    },
    {
      header: "Location",
      accessor: "location",
      render: (location) => (
        <p>
          {location || <span className="text-gray-600"> No Address Added</span>}
        </p>
      ),
    },
    {
      header: "Created Date And Time",
      accessor: "date",
      render: (_, row) => (
        <div className="flex flex-col">
          <span className="flex items-center text-xs text-black">
            <Calendar className="w-4 h-4 mr-2 text-black" /> {row.date}
          </span>
          <span className="flex items-center text-xs text-black">
            <Clock className="w-4 h-4 mr-2 text-black" /> {row.time}
          </span>
        </div>
      ),
    },
  ];

  if (initialLoad && status === "loading") {
    return <Loader />;
  }
  return (
    <>
      <Datatable
        title="Manage users"
        isloading={isTableLoading}
        columns={columns}
        data={mergedData}
        searchKeys={["detail", "provider", "location"]}
        paginationInfo={{
          currentPage: page,
          totalPages: serverTotalPages,
          entriesPerPage: entriesPerPage,
          onPageChange: handlePageChange,
          onEntriesChange: handleEntriesChange,
          onSearchChange: handleSearchChange,
        }}
      />
    </>
  );
};

export default Table;
